/**
 * SPA-side wrapper around the package's prop-driven AppShell.
 *
 * This is where every SPA-specific concern (workspace name, dark-native vs
 * darkify routing, command palette + shortcuts overlay contents, sidebar
 * Favorites/Recents/counts, TopbarActions in the right slot, global
 * keyboard shortcuts) is composed before being handed to <AppShell>.
 *
 * Local Monitor (and any future consumer) renders the package's AppShell
 * directly with its own choices — they don't share this file.
 *
 * RULE-OF-HOOKS: every hook on this component is called UNCONDITIONALLY
 * at the top. That includes `useWorkspaceName`. The previous AppShell had
 * a comment about this (gotcha caught after v0.6.0 rolled): navigating
 * between /flows ↔ /edit/:id would otherwise change the hook count and
 * React throws "Rendered fewer hooks than expected", unmounting the
 * entire tree mid-navigation. AppShell's `bare` prop handles the editor
 * case downstream — there is no early return here.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { AppShell, useShortcuts, type Crumb } from '@pipebase/ui';
import { navGroupsFor, isEditorRoute, findActiveNavItem } from './shell/nav';
import { isCloudHiddenPath, useCloud } from './cloud';
import { useWorkspaceName } from './hooks/useWorkspaceName';
import { useActiveWorkspace } from './runtime/ActiveWorkspaceContext';
import { useFeatures, type FeatureKey } from './runtime/WorkspaceFeaturesContext';
import { useSidebarCounts } from './shell/useSidebarCounts';
import { FavoritesSection } from './shell/FavoritesSection';
import { RecentsSection } from './shell/RecentsSection';
import { WorkspaceFooter } from './shell/WorkspaceFooter';
import { CommandPalette } from './shell/CommandPalette';
import { KeyboardShortcutsPanel } from './shell/KeyboardShortcutsPanel';
import { BrandMark } from './shell/BrandMark';
import { TopbarActions } from './shell/TopbarActions';
import '@pipebase/ui/tokens/shell.css';
import '@pipebase/ui/tokens/darkify.css';

/**
 * Inverted opt-in: every page is dark-native by default. Routes listed
 * here are still on the old Tailwind theme and need `darkify.css` to
 * translate utility classes into dark-theme equivalents.
 */
const LEGACY_TAILWIND_PREFIXES = [
  '/oauth-clients',
  '/value-mappings/', // trailing slash → only the edit page, not the list
  '/mappings/',
  '/runtimes/new',
  '/runtimes/', // matches /runtimes/<id> but NOT bare /runtimes
];

function isDarkNative(pathname: string): boolean {
  return !LEGACY_TAILWIND_PREFIXES.some((p) => pathname.startsWith(p));
}

const GO_TO: ReadonlyArray<readonly [string, string]> = [
  ['f', '/flows'],
  ['m', '/mesh'],
  ['d', '/'],
  ['e', '/event-designs'],
  ['r', '/monitor'],
  ['l', '/logs'],
  ['t', '/trace'],
];

export function AppShellSpa({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  // useWorkspaceName MUST be called unconditionally (Rules of Hooks).
  // See the file-level comment for why.
  const localName = useWorkspaceName();
  // The workspace's own name, the one the welcome step and Settings save on
  // the server (designer 0.7.63): the breadcrumb and the sidebar card used a
  // browser-only label and said "Workspace" beside a pill with the real name.
  const { active: activeWorkspace } = useActiveWorkspace();
  const workspaceName = activeWorkspace?.name?.trim() || localName;
  const counts = useSidebarCounts();
  const { isFeatureEnabled } = useFeatures();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const editor = isEditorRoute(location.pathname);
  const cloud = useCloud();

  // Global shortcuts. Editor routes have their own (next-editor's
  // HelpOverlay), so we skip wiring there.
  useShortcuts(
    editor
      ? []
      : [
          {
            key: 'k',
            mod: true,
            handler: () => setPaletteOpen((o) => !o),
            allowInEditable: true,
          },
          { key: '?', shift: true, handler: () => setShortcutsOpen((o) => !o) },
          // `g <key>` jumps; on Pipebase Cloud, not to a page it leaves out.
          ...GO_TO.filter(([, path]) => !cloud || !isCloudHiddenPath(path)).map(([key, path]) => ({
            sequence: ['g', key],
            handler: () => navigate(path),
          })),
        ],
  );

  const active = findActiveNavItem(location.pathname);
  // Crumbs:
  //   /          → just the workspace name (dashboard is home, no second segment).
  //   anywhere else → "<workspace> / <page label>", first crumb links to "/".
  // Workspace name is editable in Settings → General; defaults to "Workspace".
  // The product brand "Pipebase" lives in the sidebar; the topbar carries
  // the user's workspace name so it feels like *their* installation.
  const crumbs: Crumb[] =
    location.pathname === '/'
      ? [{ label: workspaceName }]
      : [
          { label: workspaceName, to: '/' },
          { label: active?.label ?? workspaceName },
        ];

  return (
    <AppShell
      navGroups={navGroupsFor(cloud)}
      brandMark={<BrandMark size="sm" />}
      rightSlot={<TopbarActions />}
      crumbs={crumbs}
      darkNative={isDarkNative(location.pathname)}
      bare={editor}
      sidebar={{
        counts,
        isFeatureEnabled: (key: string) => isFeatureEnabled(key as FeatureKey),
        favorites: <FavoritesSection />,
        recents: <RecentsSection />,
        footer: ({ collapsed }) => <WorkspaceFooter collapsed={collapsed} name={workspaceName} cloud={cloud} />,
      }}
      onOpenPalette={() => setPaletteOpen(true)}
      commandPalette={() => (
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      )}
      shortcutsPanel={() => (
        <KeyboardShortcutsPanel
          open={shortcutsOpen}
          onClose={() => setShortcutsOpen(false)}
        />
      )}
    >
      {children}
    </AppShell>
  );
}
